import {Injectable} from '@angular/core';
import type {
  ConfigIdentifier,
  JSONValue,
  PushFeedbackRequestData,
  PxApi,
  SurveyInvitationDialogDecision,
  SurveyInvitationDialogEventData,
  SurveyInvitationResultData,
  ThemeId
} from '@sap-px/pxapi';
import {ConfigService, FeatureTogglesService} from '@dcp/configurator';
import {BehaviorSubject, combineLatestWith, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {BreakpointObserver} from '@angular/cdk/layout';

const PX_MOBILE_VIEW_BREAKPOINT = '(max-width: 699.98px)';

@Injectable({
  providedIn: 'root',
})
export class PxSurveyService {
  public isInitialized$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private _pxApi: PxApi | null = null;
  private _pxApiPromise: Promise<PxApi> | null = null;
  private _themeId: ThemeId | null = null;

  constructor(
    private configService: ConfigService,
    private readonly featureTogglesService: FeatureTogglesService,
    private readonly breakpointObserver: BreakpointObserver
  ) {
    this.initializePxApi().catch((error) => {
      console.error('PxSurveyService: Failed to initialize PX API', error);
    });
  }

  private async loadPxApi(): Promise<PxApi> {
    if (this._pxApi) {
      return this._pxApi;
    }
    if (!this._pxApiPromise) {
      this._pxApiPromise = import('@sap-px/pxapi')
        .then((module) => {
          // Handle different module structures (UMD vs ESM)
          // In development: module.PxApi exists directly
          // In production: may be at module.default.PxApi or module.default (if it's a constructor)
          const PxApiClass = module.PxApi ||
                             (module as any).default?.PxApi ||
                             (typeof (module as any).default === 'function' ? (module as any).default : null);
          const ThemeIdEnum = module.ThemeId || (module as any).default?.ThemeId;

          if (!PxApiClass || typeof PxApiClass !== 'function') {
            console.error('[PxSurvey] Module structure:', Object.keys(module));
            throw new Error('PxApi class not found in module exports');
          }

          if (!ThemeIdEnum) {
            console.error('[PxSurvey] ThemeId enum not found in module');
          }

          const instance = new PxApiClass();

          // Runtime duck-type validation: ensure instance has required PxApi methods
          if (!instance ||
              typeof instance.initialize !== 'function' ||
              typeof instance.nonce !== 'function' ||
              typeof instance.openSurvey !== 'function' ||
              typeof instance.requestPush !== 'function') {
            console.error('[PxSurvey] Constructed instance missing required PxApi methods:', instance);
            throw new Error('Constructed object does not implement PxApi interface');
          }

          this._pxApi = instance;
          // Cache ThemeId to avoid redundant dynamic imports in hot paths
          // Use 'sap_horizon' if enum available, otherwise fall back to 'none' (not null) as per PxApi spec
          this._themeId = ThemeIdEnum?.sap_horizon ?? 'none';
          return this._pxApi;
        })
        .catch((error) => {
          // Reset promise so retry is possible
          this._pxApiPromise = null;
          throw new Error(`Failed to load @sap-px/pxapi module: ${error.message}`);
        });
    }
    return this._pxApiPromise;
  }

  //px-survey is lazy-loaded for performance reasons
  private async initializePxApi(): Promise<void> {
    try {
      const pxApi = await this.loadPxApi();

      const tenantInfo = {tenantId: 'SAP Store', tenantRole: 'dev'};
      const randomNonce = Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
      pxApi.nonce(randomNonce); // Set a nonce for security purposes
      pxApi.currentThemeId = this._themeId;

      const isInitialized = await pxApi.initialize(
        tenantInfo,
        this.getConfigIdentifier(),
        undefined,
        this.surveyInvitationDialogCallback.bind(this)
      );

      this.isInitialized$.next(isInitialized);
    } catch (error) {
      console.error('PxSurveyService: Error during PX API initialization', error);
      this.isInitialized$.next(false);
      // Rethrow so constructor's catch handler can also log/report
      throw error;
    }
  }

  getConfigIdentifier(): ConfigIdentifier {
    const configIdentifier = this.configService.getObject('pxSurvey.configIdentifier');
    return configIdentifier;
  }

  public async startSurvey(): Promise<void> {
    if (this.isInitialized$.value && this._pxApi) {
      this._pxApi.currentThemeId = this._themeId;
      this._pxApi.openSurvey({
        appId: 'SAP Store',
        appTitle: 'SAP Store',
        appVersion: '1.0.0',
        languageTag: 'en-us',
        theme: this._themeId,
      });
    }
  }

  public async pushCheckoutSurvey(payload: JSONValue): Promise<void> {
    if (this.isInitialized$.value && this._pxApi) {
      const pushData: PushFeedbackRequestData = {
        areaId: 'checkout',
        triggerName: 'checkout_completed',
        shortText: 'User completed checkout',
        payload: payload,
      };
      this._pxApi.currentThemeId = this._themeId;
      this._pxApi.requestPush(pushData);
    }
  }

  resolveSurveyInvitation: SurveyInvitationDialogDecision;

  async surveyInvitationDialogCallback(
    eventData: SurveyInvitationDialogEventData
  ): Promise<SurveyInvitationResultData> {
    const surveyInvitationDialogResponse = new Promise<SurveyInvitationResultData>(
      (resolve) => (this.resolveSurveyInvitation = resolve)
    );
    this.provideFeedback();
    return surveyInvitationDialogResponse;
  }

  async provideFeedback(): Promise<void> {
    const resultData = {
      appContextData: {
        appId: 'SAP Store',
        appVersion: '1.0.0',
        languageTag: 'en-us',
        theme: this._themeId,
      },
      surveyUser: true,
    };
    if (this.resolveSurveyInvitation) {
      this.resolveSurveyInvitation(resultData);
    }
  }

  shouldEnablePxSurvey(featureToggleName: string): Observable<boolean> {
    return this.breakpointObserver.observe([PX_MOBILE_VIEW_BREAKPOINT]).pipe(
      combineLatestWith(this.featureTogglesService.isEnabled(featureToggleName), this.isInitialized$),
      map(([result, isEnabled, isInitialized]) => isEnabled && isInitialized && !result.matches)
    );
  }
}
